import type { ValidationError } from '@/types/api'
import { messagesNow } from '@/i18n'
import type { Block } from '@/types/blocks'
import { typeLabel } from '@/features/editor/components/studio/blockTypeLabels'
import { messages } from './messages'

/**
 * Бэк отдаёт ошибки публикации по-английски и технически, например
 * `{ location: 'Course', message: 'Description is required' }`. Диалог
 * печатал это дословно — жалоба заказчика «часто там системные текста».
 *
 * Словарь известных сообщений — `Map`, а не объектный литерал: у литерала
 * ключ `constructor` вернёт функцию, и фолбэк на дословный показ не
 * сработает (тот же приём, что в `imageFailureText`).
 *
 * Начат РОВНО одним подтверждённым случаем. Новые записи добавлять только
 * когда сообщение увидено живьём — непроверенный перевод хуже оригинала.
 */
type Msgs = (typeof messages)['ru']
const KNOWN_MESSAGES = new Map<string, (m: Msgs) => { what: string; hint: string }>([
  [
    'Description is required',
    (m) => ({ what: m.publishNoDescription, hint: m.publishNoDescriptionHint }),
  ],
  // Текст — наш же валидатор api (`GamifiedCourseValidator._validate_block`),
  // не догадка. На «Обновить» студия видео не гасит, и автор видит его отсюда.
  ['Video is required', (m) => ({ what: m.publishNoVideo, hint: m.publishNoVideoHint })],
  [
    'Match block must have at least 2 pairs',
    (m) => ({ what: m.publishMatchFewPairs, hint: m.publishMatchFewPairsHint }),
  ],
  // Пустой блок статьи (PRD #71, ф.3) сохраняется, но не публикуется — тот же
  // валидатор api.
  ['Article is required', (m) => ({ what: m.publishNoArticle, hint: m.publishNoArticleHint })],
])

const LESSON_BLOCK_RE = /^Lesson\s+(\d+)\s*\/\s*block\s+(\d+)$/i
// Формат валидатора api (`GamifiedCourseValidator._validate_block`):
// `Lesson '<имя>' (ID: n) -> Block (ID: m, Type: <тип>)`. Имя жадно — в нём
// бывает апостроф.
const API_LESSON_BLOCK_RE = /^Lesson '(.*)' \(ID: \d+\) -> Block \(ID: \d+, Type: (\w+)\)$/

// Подписи типов — те же, что у блоков в студии (`typeLabel`). Квизу из одного
// типа не узнать подвид (один/несколько ответов, да/нет) — у него общая подпись.
const KNOWN_TYPES = new Set(['info', 'match', 'article', 'video', 'design'])
const blockTypeText = (type: string, t: Msgs): string => {
  if (type === 'quiz') return t.publishWhereQuiz
  return KNOWN_TYPES.has(type) ? typeLabel({ type } as Block) : type
}

const readWhere = (location: string): string => {
  const t = messagesNow(messages)
  if (location === 'Course') return t.publishWhereCourse
  const m = LESSON_BLOCK_RE.exec(location)
  if (m) return t.publishWhereLessonBlock(m[1], m[2])
  const api = API_LESSON_BLOCK_RE.exec(location)
  if (api) return t.publishWhereLessonBlockOf(api[1], blockTypeText(api[2], t))
  return location
}

export interface PublishErrorText {
  where: string
  what: string
  hint?: string
}

export const publishErrorText = (e: ValidationError): PublishErrorText => {
  const where = readWhere(e.location)
  const known = KNOWN_MESSAGES.get(e.message)?.(messagesNow(messages))
  if (!known) return { where, what: e.message }
  return { where, what: known.what, hint: known.hint }
}
